import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@shared/contexts/ThemeContext';
import PgkbLogo from '../components/PgkbLogo';
import RateLimitBanner from '../components/RateLimitBanner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import * as Icons from 'lucide-react';

interface AuthLayoutProps {
  children: React.ReactNode;
  maxWidth?: string;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children, maxWidth = 'max-w-md' }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation('landing');

  return (
    <div className="bg-surface-light dark:bg-surface-dark text-slate-900 dark:text-white min-h-screen flex flex-col font-sans">
      {/* Header */}
      <header className="relative z-20 flex justify-between items-center px-4 sm:px-8 h-20 bg-surface-card-light dark:bg-surface-card-dark border-b border-surface-border-light dark:border-surface-border-dark">
        <Link to="/landing" className="flex items-center gap-2.5 group">
          <PgkbLogo size="sm" />
        </Link>
        <nav className="flex min-w-0 items-center gap-1 sm:gap-6">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-xl text-text-muted-light dark:text-text-muted-dark hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-all"
            aria-label={t('common:labels.toggleTheme')}
          >
            {theme === 'dark' ? <Icons.Sun size={20} /> : <Icons.Moon size={20} />}
          </button>
          <LanguageSwitcher variant="minimal" />
          <Link
            to="/landing"
            className="hidden sm:block text-sm font-bold text-text-muted-light dark:text-text-muted-dark hover:text-brand-600 dark:hover:text-brand-400 transition-colors uppercase tracking-widest"
          >
            {t('navigation:breadcrumbs.home')}
          </Link>
          {isLoginPage ? (
            <Link
              to="/register"
              className="whitespace-nowrap rounded-xl bg-brand-600 px-3 py-2.5 text-sm font-bold text-on-accent-light shadow-lg shadow-brand-200 transition-all hover:bg-brand-700 active:scale-95 sm:px-6 dark:shadow-none"
            >
              {t('nav.getStarted')}
            </Link>
          ) : (
            <Link
              to="/login"
              className="text-sm font-bold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors uppercase tracking-widest"
            >
              {t('nav.login')}
            </Link>
          )}
        </nav>
      </header>
      <RateLimitBanner />

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-brand-500/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-brand-500/5 rounded-full blur-3xl pointer-events-none" />

        <div
          className={`${maxWidth} w-full relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-700`}
        >
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-surface-border-light dark:border-surface-border-dark bg-surface-card-light dark:bg-surface-card-dark">
        <div className="flex flex-col items-center gap-3">
          <div className="flex flex-wrap justify-center gap-6 text-[10px] font-black text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-[0.2em]">
            <Link
              to="/terms"
              className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
            >
              {t('footer.terms')}
            </Link>
            <Link
              to="/privacy"
              className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to="/cookies"
              className="hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
            >
              {t('footer.cookies')}
            </Link>
          </div>
          <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-[0.2em]">
            {t('footer.copyright')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AuthLayout;
